import { Cookies } from "react-cookie";
import isomorphicFetch from "isomorphic-fetch";
import { UnauthorizedError } from "~/utils/errors/UnauthorizedError.js";
import Angie from "./angie.jsx";
import { NotFoundError, StorageOverusedError } from "./errors/index.js";

const cookie = new Cookies();

function FetchException(message, status_code, response = {}) {
  this.message = message;
  this.response = response;
  this.status_code = status_code;
  this.name = `FetchException`;
}

function RedirectException(response = {}) {
  this.message = "Resource Moved";
  this.response = response;
  this.status_code = 301;
  this.name = "RedirectException";
}

const prepareResponse = (response) => {
  return response.text().then((text) => {
    let result = {};
    let parsingError;

    try {
      result = JSON.parse(text);
    } catch (err) {
      parsingError = err;
    }

    return { response, parsedBody: result, parsingError };
  });
};

export const fetch = (args) => {
  const { url, body, method, cors, detailed, withHeaders, prefetchNext, signal } = args;

  const request_token = cookie.get(window.csrfValidatorName);
  const newbody = JSON.stringify(body);
  const newargs = {
    body: newbody,
    method: method || `get`,
    headers: {
      Accept: `application/json, text/plain, * / *`,
    },
  };

  if (!cors) {
    newargs.credentials = `include`; //pass cookies, for authentication
    if (request_token) {
      newargs.headers[`X-Angie-CsrfValidator`] = request_token;
    }
  } else {
    newargs.mode = `cors`;
  }

  if (method === `get`) {
    newargs.headers[`Cache-Control`] = `public, max-age=0`;
    newargs.headers.Pragma = `public`;
  }

  if ([`PUT`, `POST`, `DELETE`].indexOf(newargs.method.toUpperCase()) > -1) {
    newargs.headers[`Content-Type`] = `application/json; charset=utf-8`;
  }

  if (prefetchNext) {
    newargs.headers[`X-Angie-Prefetch`] = true;
  }

  if (signal) {
    newargs.signal = signal;
  }

  return isomorphicFetch(url, newargs)
    .then(prepareResponse)
    .then(({ response, parsedBody }) => {
      if (!response.ok) {
        if (response.status === 401) {
          throw new UnauthorizedError("Unauthorized Error", parsedBody);
        }
        if (response.status === 404) {
          throw new NotFoundError();
        }
        if (response.status === 400 && parsedBody?.error === "storage_overused") {
          throw new StorageOverusedError(parsedBody.message, parsedBody.error_content);
        }
        throw new FetchException(response.statusText, response.status, parsedBody);
      }

      if (response.redirected) {
        throw new RedirectException(parsedBody);
      }

      if (response.headers) {
        const response_version = response.headers.get("x-angie-frontendapplicationversion");

        if (response_version) {
          if (!Angie.frontend_application_version) {
            Angie.frontend_application_version = response_version;
          } else {
            if (response_version !== Angie.frontend_application_version) {
              Angie.page_reload_needed = true;
            }
          }
        }
      }

      if (detailed) {
        return response;
      }

      if (withHeaders) {
        let headers = {};
        if (response.headers) {
          response.headers.forEach((value, key) => {
            headers[key.toLowerCase()] = value;
          });
        }
        return {
          _parsedBody: parsedBody,
          _headers: headers,
        };
      }

      return parsedBody;
    });
};
